import {
  ActivityFilterKey,
  FeatureName,
  useActivityListFilter,
  useCurrentWorkspace,
  useIsFeatureEnabled,
  useSetActivityListFilter,
} from '@gripp/shared-logic';
import {
  ActivitySearchInput,
  countFilters,
  FilterHeaders,
  SlideAction,
  useSlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { Pressable, StyleSheet, View } from 'react-native';
import { useNavigate } from 'react-router';
import { AddButton } from '../../shared/components/addButton/addButton';
import { ActivityFiltersOld } from './activityFilters-old';

interface ActivityFiltersProps {
  assetId?: string;
  showAddButton?: boolean;
  showFilterButton?: boolean;
  onFilterChange?: () => void;
}

export const ActivityFilters = ({
  assetId,
  showAddButton = true,
  showFilterButton = true,
  onFilterChange,
}: ActivityFiltersProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  if (!isCropCircleEnabled) {
    return <ActivityFiltersOld assetId={assetId} />;
  }
  return (
    <ActivityFiltersBase
      assetId={assetId}
      showAddButton={showAddButton}
      showFilterButton={showFilterButton}
      onFilterChange={onFilterChange}
    />
  );
};

export const ActivityFiltersBase = ({
  assetId,
  showAddButton = true,
  showFilterButton = true,
  onFilterChange,
}: ActivityFiltersProps) => {
  const navigate = useNavigate();
  const { currentViewMode, onSlideChange } = useSlideLayout();
  const workspace = useCurrentWorkspace();

  const filterKey = assetId
    ? ActivityFilterKey.AssetActivity
    : ActivityFilterKey.Activity;
  const { data: activityFilter } = useActivityListFilter(filterKey);
  const { mutateAsync: setActivityListFilter } =
    useSetActivityListFilter(filterKey);
  const filterCount = countFilters(activityFilter);

  const onFilterPress = async () => {
    if (onFilterChange) {
      onFilterChange();
      return;
    }

    if (assetId) {
      await onSlideChange(SlideAction.NEXT);
      navigate(`/assets/${assetId}/detail/filters`);
      return;
    }

    if (currentViewMode === ViewMode.SINGLE) {
      await onSlideChange(SlideAction.DUAL);
    }

    navigate('/activities/filters');
  };

  const onNewActivity = async () => {
    if (currentViewMode === ViewMode.SINGLE) {
      await onSlideChange(SlideAction.DUAL);
    }

    navigate(`/activities/new`);
  };

  return (
    <View style={styles.filtersContainer}>
      <FilterHeaders
        currentSearchKeyword={activityFilter?.keyword}
        searchComponent={<ActivitySearchInput filterKey={filterKey} />}
        onFilterPress={onFilterPress}
        onSearchClose={() =>
          setActivityListFilter({ ...activityFilter, keyword: '' })
        }
        filterCount={filterCount}
        Routes={{}}
        showFilterButton={showFilterButton}
      />

      {showAddButton && workspace && (
        <Pressable>
          <AddButton onPress={onNewActivity} />
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  filtersContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
  },
  searchButton: { padding: 8, borderRadius: 8 },
  filterButton: { padding: 8, borderRadius: 8 },
  filterButtonContent: { position: 'relative' },
  badgeContainer: { position: 'absolute', top: -6, right: -6 },
});
